import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { inr, num } from '../lib/format';
import { Card, CardHead, Empty, Notice } from '../components/ui';
import { BarList, LineChart } from '../components/Chart';

interface Dashboard {
  users: { total: number; newLast24h: number };
  listings: { live: number };
  queues: { kycPending: number; categoriesPending: number; policeVerificationPending: number; reportsOpen: number };
  revenue: { capturedCount: number; grossPaise: number; gstCollectedPaise: number };
  blockedBonuses: { count: number; totalPaise: number; note: string };
}

interface Analytics {
  series: Array<{ day: string; signups: number; listings: number; payments: number; revenuePaise: number }>;
  listingsByStatus: Array<{ status: string; count: number }>;
  topCategories: Array<{ name: string; count: number }>;
}

function Stat({
  label,
  value,
  icon,
  tint,
  color,
  sub,
}: {
  label: string;
  value: string;
  icon: string;
  tint: string;
  color: string;
  sub?: string;
}) {
  return (
    <Card>
      <div className="stat">
        <div className="stat__icon" style={{ background: tint, color }}>
          {icon}
        </div>
        <div>
          <div className="stat__label">{label}</div>
          <div className="stat__value">{value}</div>
          {sub && <div className="stat__delta stat__delta--flat">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

function QueueCard({ to, label, count, icon }: { to: string; label: string; count: number; icon: string }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <Card>
        <div className="stat">
          <div
            className="stat__icon"
            style={{
              background: count > 0 ? 'var(--tint-orange)' : 'var(--tint-green)',
              color: count > 0 ? 'var(--orange-deep)' : 'var(--success)',
            }}
          >
            {icon}
          </div>
          <div>
            <div className="stat__label">{label}</div>
            <div className="stat__value">{count}</div>
            <div className="stat__delta" style={{ color: count > 0 ? 'var(--orange-deep)' : 'var(--ink-faint)' }}>
              {count > 0 ? 'Needs review →' : 'All clear'}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function DashboardPage() {
  const dash = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<Dashboard>('/admin/dashboard') });
  const chart = useQuery({ queryKey: ['analytics', 30], queryFn: () => api.get<Analytics>('/admin/analytics?days=30') });

  if (dash.isError) {
    return <Empty icon="⚠️" title="Could not load the dashboard" body={(dash.error as Error).message} />;
  }

  const d = dash.data;

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      {d && d.blockedBonuses.count > 0 && (
        <Notice tone="warn" title={`${inr(d.blockedBonuses.totalPaise)} in bonuses accrued but not paid`}>
          {d.blockedBonuses.note} <Link to="/bonuses">Review the ledger →</Link>
        </Notice>
      )}

      <div className="grid grid--stats">
        <Stat
          label="Users"
          value={d ? num(d.users.total) : '—'}
          sub={d ? `${d.users.newLast24h} in the last 24h` : undefined}
          icon="☖"
          tint="var(--tint-blue)"
          color="var(--blue)"
        />
        <Stat label="Live listings" value={d ? num(d.listings.live) : '—'} icon="▤" tint="var(--tint-green)" color="var(--success)" />
        <Stat
          label="Revenue captured"
          value={d ? inr(d.revenue.grossPaise) : '—'}
          sub={d ? `${num(d.revenue.capturedCount)} payments` : undefined}
          icon="₹"
          tint="var(--tint-purple)"
          color="var(--purple)"
        />
        <Stat
          label="GST collected"
          value={d ? inr(d.revenue.gstCollectedPaise) : '—'}
          icon="⎙"
          tint="var(--tint-amber)"
          color="#a16207"
        />
      </div>

      <div>
        <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, marginBottom: 'var(--s3)' }}>Queues</div>
        <div className="grid grid--stats">
          <QueueCard to="/kyc" label="KYC awaiting review" count={d?.queues.kycPending ?? 0} icon="⛉" />
          <QueueCard
            to="/police-verifications"
            label="Police verification"
            count={d?.queues.policeVerificationPending ?? 0}
            icon="⛨"
          />
          <QueueCard
            to="/moderation/categories"
            label="Category requests"
            count={d?.queues.categoriesPending ?? 0}
            icon="⌸"
          />
          <QueueCard to="/reports" label="Reported content" count={d?.queues.reportsOpen ?? 0} icon="⚑" />
        </div>
      </div>

      <Card>
        <CardHead title="Last 30 days" sub="Signups, new listings and captured payments" />
        <div style={{ paddingTop: 'var(--s4)' }}>
          {chart.data ? (
            <LineChart
              labels={chart.data.series.map((s) => s.day)}
              series={[
                { label: 'Signups', color: 'var(--blue)', values: chart.data.series.map((s) => s.signups) },
                { label: 'Listings', color: 'var(--orange)', values: chart.data.series.map((s) => s.listings) },
                { label: 'Payments', color: 'var(--success)', values: chart.data.series.map((s) => s.payments) },
              ]}
            />
          ) : (
            <div className="skeleton" style={{ height: 210 }} />
          )}
        </div>
      </Card>

      <div className="grid grid--2">
        <Card>
          <CardHead title="Listings by status" />
          <div style={{ paddingTop: 'var(--s4)' }}>
            {chart.data?.listingsByStatus.length ? (
              <BarList
                rows={chart.data.listingsByStatus.map((r) => ({
                  label: r.status.replace(/_/g, ' ').toLowerCase(),
                  value: r.count,
                }))}
              />
            ) : (
              <Empty icon="▤" title="No listings yet" />
            )}
          </div>
        </Card>

        <Card>
          <CardHead title="Busiest categories" sub="Live listings" />
          <div style={{ paddingTop: 'var(--s4)' }}>
            {chart.data?.topCategories.length ? (
              <BarList rows={chart.data.topCategories.map((r) => ({ label: r.name, value: r.count }))} />
            ) : (
              <Empty icon="⌗" title="No live listings yet" />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
